import {Injectable} from '@angular/core';
import {State, Action, StateContext, Selector, Store} from '@ngxs/store';
import {SharedStateModel} from '@core/models/store/shared-state.model';
import {CostDescriptionModel} from '@core/models/cost-description.model';
import {
  FetchOrgUserPhoto,
  GetOrgUserById,
  GetVehicleCategories,
  LoadCountryById,
  LoadOrgById,
  ResetSharedState,
  SearchCost,
  SetSkeletonLoader,
  SetSpinner,
  UpdateDashboardRows,
  SetDownloadProgress,
  ClearDownloadProgress,
  SetBulkUploadResult,
  ClearBulkUploadResult
} from '@core/store/shared.actions';
import {CostService} from '@core/services/cost.service';
import {catchError, map, tap} from 'rxjs/operators';
import {SetNotificationAction} from '@core/store/notification/notification.actions';
import {AlertDialogType} from '@shared/models';
import { OrganizationService } from 'app/modules/organization/services/organization.service';
import { DashboardWidgetConfig, NavigationConfig, Organization } from 'app/modules/organization/models/organization';
import { Country } from '@shared/models/country.model';
import {
  ContractManagementStateModel
} from '../../modules/contract-management/models/state/contract-management-state.model';
import {VehicleCategory} from '@shared/models/vehicle-category.model';
import { DashboardRow } from 'app/modules/dashboard/models/dashboard-rows.model';
import {WidgetCategoryConfig} from '../../modules/dashboard/models/widget-config.model';
import { OrgUser } from 'app/modules/organization/models/org-user';
import { FileHandlerService } from '@shared/services/file-handler.service';

const defaults: SharedStateModel = {
  costs: [],
  spinner: false,
  country: null,
  organization: null,
  spinnerMessage: '',
  vehicleCategories: [],
  skeletonLoading: true,
  orgUser: null,
  orgUserPhoto: null,
  downloadProgress: {},
  downloadFileNames: {},
  bulkUploadResult: {
    resultReady: false,
    file: null,
    info: null
  }
};

@State<SharedStateModel>({
  name: 'shared',
  defaults
})
@Injectable()
export class SharedState {

  constructor(private costService: CostService,
              private organizationService: OrganizationService,
              private fileHandlerService: FileHandlerService,
              public store: Store,
            ) {
  }

  @Selector()
  static searchCosts(state: SharedStateModel): CostDescriptionModel[] {
    return state.costs;
  }

  @Selector()
  static getSpinnerStatus(state: SharedStateModel): {spinner: boolean; spinnerMessage: string} {
    return {spinner: state.spinner, spinnerMessage: state.spinnerMessage};
  }

  @Selector()
  static getUserOrg(state: SharedStateModel): Organization {
    return state.organization;
  }


  @Selector()
  static getNavigationConfig(state: SharedStateModel): NavigationConfig[] {
    return state.organization?.orgConfig?.navigationConfig || [];
  }

  @Selector()
  static getDashboardConfig(state: SharedStateModel): DashboardWidgetConfig[] {
    return state.organization?.orgConfig?.dashboardWidgetsConfig || [];
  }

  @Selector()
  static getDashboardRows(state: SharedStateModel): (userId: string) => DashboardRow[] {
    return (userId: string): DashboardRow[] =>
    state.organization?.orgConfig?.dashboardRows[userId] || [];
  }

  @Selector()
  static getWidgetCategoryConfig(state: SharedStateModel): WidgetCategoryConfig[] {
    return state.organization?.orgConfig?.widgetCategories || [];
  }

  @Selector()
  static getUserCountry(state: SharedStateModel): Country {
    return state.country;
  }

  @Selector()
  static getVehicleCategories(state: SharedStateModel): VehicleCategory[] {
    return state.vehicleCategories;
  }

  @Selector()
  static getSkeletonLoaderStatus(state: SharedStateModel): boolean {
    return state.skeletonLoading;
  }

  @Selector()
  static getOrgUser(state: SharedStateModel): OrgUser {
    return state.orgUser;
  }

  @Selector()
  static getOrgUserPhoto(state: SharedStateModel): string {
    return state.orgUserPhoto;
  }

  @Selector()
  static getDownloadProgress(state: SharedStateModel): { [downloadType: string]: number } {
    return state.downloadProgress;
  }

  @Selector()
  static getDownloadFileNames(state: SharedStateModel): { [downloadType: string]: string } {
    return state.downloadFileNames;
  }

  @Selector()
  static getBulkUploadResult(state: SharedStateModel) {
    return state.bulkUploadResult;
  }

  @Action(SearchCost)
  searchCost({patchState, dispatch}: StateContext<SharedStateModel>, {keyword}: SearchCost) {
    return this.costService.searchCost(keyword)
      .pipe(
        map((costs: CostDescriptionModel[]) => patchState({costs})),
        catchError(err => dispatch(new SetNotificationAction('Error loading cost', AlertDialogType.ERROR)))
      );
  }

  @Action(SetSpinner)
  setSpinner({patchState}: StateContext<SharedStateModel>, {isEnable, message}: SetSpinner) {
    return patchState({spinner: isEnable, spinnerMessage: message});
  }

  @Action(LoadOrgById)
  loadUserOrg({patchState, dispatch}: StateContext<SharedStateModel>, {orgId}: LoadOrgById) {
    return this.organizationService.getOrganization(orgId)
      .pipe(
        map(organization => patchState({organization})),
        catchError(_ => dispatch(new SetNotificationAction('Unable to load User Organization',
          AlertDialogType.ERROR)))
      );
  }

  @Action(LoadCountryById)
  loadUserOrgCountry({patchState, dispatch, getState}: StateContext<SharedStateModel>, {countryId}: LoadCountryById) {
    const organization = getState().organization;
    return this.organizationService.fetchCountryById(countryId ?? organization?.countryId)
      .pipe(
        map(country => patchState({country})),
        catchError(_ => dispatch(new SetNotificationAction('Unable to load User org Country',
          AlertDialogType.ERROR)))
      );
  }

  @Action(ResetSharedState)
  resetState({setState}: StateContext<SharedStateModel>) {
    return setState(defaults);
  }

  @Action(GetVehicleCategories)
  getVehicleCategories({dispatch, patchState}: StateContext<SharedStateModel>,
                       {}: GetVehicleCategories) {
    return this.organizationService.getVehicleCategories()
      .pipe(
        tap((vehicleCategories) => {
          patchState({vehicleCategories});
        }),
        catchError(() => dispatch(new SetNotificationAction('Failed fetch vehicle categories', AlertDialogType.ERROR)))
      );
  }

  @Action(SetSkeletonLoader)
  setSkeletonLoader({patchState}: StateContext<SharedStateModel>, {isEnable}: SetSkeletonLoader) {
    return patchState({skeletonLoading: isEnable});
  }

  @Action(UpdateDashboardRows)
  updateDashboardRows({dispatch, patchState, getState}: StateContext<SharedStateModel>, {dashboardRows}: UpdateDashboardRows) {
    const orgId = getState().organization.id;
    const {user: {userId}} = this.store.selectSnapshot(state => state.auth);
    return this.organizationService.updateDashboardRows(orgId, userId, dashboardRows)
    .pipe(
      tap(() => {
        dispatch(new LoadOrgById(orgId));
        dispatch(new SetNotificationAction(`Dashboard updated successfully`, AlertDialogType.SUCCESS));
      }),
      catchError((err) => dispatch(new SetNotificationAction('Dashboard Rows Update Failed', AlertDialogType.ERROR)))
    );
  }

  @Action(GetOrgUserById)
  getOrgUserById({patchState, dispatch}: StateContext<SharedStateModel>, {orgId, orgUserId  }: GetOrgUserById) {
    return this.organizationService.getOrgUserById(orgId, orgUserId)
      .pipe(
        map(orgUser => patchState({orgUser})),
        catchError(() => dispatch(new SetNotificationAction('Failed to fetch organization user', AlertDialogType.ERROR)))
      );
  }

  @Action(FetchOrgUserPhoto)
  fetchOrgUserPhoto({patchState, dispatch}: StateContext<SharedStateModel>, {orgUserId}: FetchOrgUserPhoto) {
    return this.fileHandlerService.fetchFile(orgUserId)
      .pipe(
        map(photo => patchState({orgUserPhoto: photo.url})),
      );
  }

  @Action(SetDownloadProgress)
  setDownloadProgress({patchState, getState}: StateContext<SharedStateModel>, {progress, downloadType, fileName}: SetDownloadProgress) {
    const currentProgress = getState().downloadProgress;
    const currentFileNames = getState().downloadFileNames;

    const newState: Partial<SharedStateModel> = {
      downloadProgress: {
        ...currentProgress,
        [downloadType]: progress
      }
    };

    // Store custom filename if provided (only when starting download at 0%)
    if (fileName && progress === 0) {
      newState.downloadFileNames = {
        ...currentFileNames,
        [downloadType]: fileName
      };
    }

    return patchState(newState);
  }

  @Action(ClearDownloadProgress)
  clearDownloadProgress({patchState, getState}: StateContext<SharedStateModel>, {downloadType}: ClearDownloadProgress) {
    const currentProgress = getState().downloadProgress;
    const currentFileNames = getState().downloadFileNames;

    // Remove the download type from progress and fileNames
    const { [downloadType]: removed, ...remainingProgress } = currentProgress;
    const { [downloadType]: removedFileName, ...remainingFileNames } = currentFileNames;

    return patchState({
      downloadProgress: remainingProgress,
      downloadFileNames: remainingFileNames
    });
  }

  @Action(SetBulkUploadResult)
  setBulkUploadResult({patchState}: StateContext<SharedStateModel>, {bulkUploadResult}: SetBulkUploadResult) {
    return patchState({bulkUploadResult});
  }

  @Action(ClearBulkUploadResult)
  clearBulkUploadResult({patchState}: StateContext<SharedStateModel>) {
    return patchState({
      bulkUploadResult: {
        resultReady: false,
        file: null,
        info: null
      }
    });
  }
}
